import { translationsJson } from 'locales/i18n';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import {
  ScrollRestoration,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import {
  getIframeColors,
  onIframeScrollIntoView,
  onResize2,
} from 'utils/embed';
import { isRenderedInsideEmbed } from 'utils/isRenderedInsideEmbed';
import Footer from '../Footer';
import Navbar from '../Navbar/Navbar';

const firstStepPages = [
  'registration',
  'buy-tickets',
  'calendar',
  'reserve-seats',
];

const Layout: React.FC<{}> = () => {
  const { pathname, state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const userComesFromPreviousPage = state?.comesFromPreviousPage;

  const splittedPathname = pathname.split('/');
  const isFirstStepPage = firstStepPages.includes(
    splittedPathname[splittedPathname.length - 1],
  );

  useEffect(() => {
    if (!isRenderedInsideEmbed) return;

    const resizeObserver = new ResizeObserver(() => {
      onResize2();
    });

    resizeObserver.disconnect();

    const sectionContainer = document.querySelector('.section-container');

    if (!sectionContainer) return;

    resizeObserver.observe(sectionContainer, {
      childList: true,
      subtree: true,
    } as any);

    return () => {
      resizeObserver.disconnect();
    };
  }, [pathname]);

  useEffect(() => {
    if (!isFirstStepPage || userComesFromPreviousPage) {
      onIframeScrollIntoView();
    }
  }, [pathname, userComesFromPreviousPage]);

  useEffect(() => {
    const language = searchParams.get('language');
    if (language && Object.keys(translationsJson).includes(language)) {
      i18n.changeLanguage(language);
      searchParams.delete('language');
      setSearchParams(searchParams);
    }
  }, []);

  const setIframeColors = colors => {
    if (!isRenderedInsideEmbed) return;

    const root = document.querySelector(':root') as HTMLElement;

    colors.forEach(color => {
      if (!color) return;

      const name = color.split(':')[0].trim();
      const value = color.split(':')[1].trim();
      root.style.setProperty(name, value);

      if (name === '--bodyBackground') {
        document.body.style.background = value;
      }
    });
  };

  useEffect(() => {
    getIframeColors(setIframeColors);
  }, []);

  return (
    <>
      <ScrollRestoration />
      <Navbar />
      <ToastContainer
        icon={({ type }) => {
          switch (type) {
            case 'info':
              return <Info />;
            case 'error':
              return <CircleAlert />;
            case 'success':
              return <CircleCheck />;
            case 'warning':
              return <TriangleAlert />;
            default:
              return null;
          }
        }}
      />
      <Outlet />
      <Footer />
    </>
  );
};

export default Layout;
